import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  useGetRecordingAccessQuery,
  useGetRecordingStatusQuery,
  useStartRecordingMutation,
  useStopRecordingMutation,
} from '../../services/aiSupervisor.api';

export type SupervisorNotification = { type: 'request_sent' } | { type: 'accepted' } | { type: 'rejected' };

const MINIMUM_REMAINING_MINUTES = 10;
const MINUTE_MS = 60 * 1000;
const PENDING_DEBIT_STORAGE_PREFIX = 'ai-supervisor:pending-debit';

interface PendingSupervisorDebit {
  consultationId?: string | number;
  expiresAt: number;
  minutes: number;
  roomName: string;
}

const normalizeRemaining = (remaining: number | undefined): number | undefined => {
  if (typeof remaining !== 'number' || !Number.isFinite(remaining)) {
    return undefined;
  }

  return Math.max(0, remaining);
};

const getPendingDebitStorageKey = (roomName: string) => `${PENDING_DEBIT_STORAGE_PREFIX}:${roomName}`;

const removePendingDebitFromStorage = (storageKey: string) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.removeItem(storageKey);
  } catch {
    // Браузер может запретить localStorage, например в приватном режиме.
  }
};

const getTimestamp = (value: string | undefined): number | undefined => {
  if (!value) {
    return undefined;
  }

  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : undefined;
};

const readPendingDebit = (
  storageKey: string,
  roomName: string,
  consultationId: string | number | undefined,
): PendingSupervisorDebit | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) {
      return null;
    }

    const pending = JSON.parse(raw) as PendingSupervisorDebit;
    const isConsultationMatch =
      consultationId === undefined || String(pending.consultationId) === String(consultationId);
    const isValid =
      pending.roomName === roomName &&
      isConsultationMatch &&
      Number.isFinite(pending.minutes) &&
      pending.minutes > 0 &&
      Number.isFinite(pending.expiresAt) &&
      pending.expiresAt > Date.now();

    if (isValid) {
      return pending;
    }

    removePendingDebitFromStorage(storageKey);
  } catch {
    removePendingDebitFromStorage(storageKey);
  }

  return null;
};

interface UseMeetSupervisorArgs {
  consultationId?: string | number;
  roomName: string;
  scheduledEnd?: string;
  isGroupConsultationFlag: boolean;
  isPsychologist: boolean;
  sendSupervisorRequest?: () => void;
  sendRecordingStatusUpdate?: (status: 'started' | 'stopped') => void;
}

export function useMeetSupervisor({
  consultationId,
  roomName,
  scheduledEnd,
  isGroupConsultationFlag,
  isPsychologist,
  sendSupervisorRequest,
  sendRecordingStatusUpdate,
}: UseMeetSupervisorArgs) {
  const [isConfirmCloseOpen, setIsConfirmCloseOpen] = useState(false);
  const [isConfirmStartSupervisorOpen, setIsConfirmStartSupervisorOpen] = useState(false);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [supervisorNotification, setSupervisorNotification] = useState<SupervisorNotification | null>(null);
  const [supervisorSend, setSupervisorSend] = useState(false);
  const [notificationClosed, setNotificationClosed] = useState(false);
  const [virtualRemaining, setVirtualRemaining] = useState<number | undefined>();
  const [pendingDebit, setPendingDebit] = useState<PendingSupervisorDebit | null>(null);
  const [isBalanceTracking, setIsBalanceTracking] = useState(false);
  const [isBalanceWarning, setIsBalanceWarning] = useState(false);

  const [startRecording, { isLoading: isStarting, data: startData }] = useStartRecordingMutation();
  const [stopRecording, { isLoading: isStopping }] = useStopRecordingMutation();
  const { data: statusData, refetch: refetchRecordingStatus } = useGetRecordingStatusQuery(
    { recordingId: roomName },
    {
      skip: !roomName,
      pollingInterval: 20000,
    },
  );
  const { data: supervisorAccessData, refetch: refetchRecordingAccess } = useGetRecordingAccessQuery(undefined, {
    skip: !isPsychologist,
  });

  const balanceStartedAtRef = useRef<number | null>(null);
  const balanceInitialRef = useRef<number | null>(null);
  const balanceAdjustmentRef = useRef(0);
  const lastServerBalanceRef = useRef<number | null>(null);
  const balanceWarningCheckRef = useRef(false);
  const autoStopInFlightRef = useRef(false);
  const lastKnownActiveRef = useRef(false);
  const pendingDebitStorageKey = useMemo(() => getPendingDebitStorageKey(roomName), [roomName]);
  const scheduledEndAt = useMemo(() => getTimestamp(scheduledEnd), [scheduledEnd]);

  const calculateVirtualRemaining = useCallback((now = Date.now()) => {
    const initialBalance = balanceInitialRef.current;
    const startedAt = balanceStartedAtRef.current;
    if (initialBalance === null || startedAt === null) {
      return undefined;
    }

    const elapsedMinutes = Math.max(0, Math.ceil((now - startedAt) / MINUTE_MS));
    return Math.max(0, initialBalance + balanceAdjustmentRef.current - elapsedMinutes);
  }, []);

  const updateVirtualRemaining = useCallback(
    (remaining: number, startedAt: number, reset = false) => {
      const normalized = normalizeRemaining(remaining);
      if (normalized === undefined) {
        return undefined;
      }

      if (reset || balanceInitialRef.current === null || balanceStartedAtRef.current === null) {
        balanceInitialRef.current = normalized;
        balanceAdjustmentRef.current = 0;
        balanceStartedAtRef.current = startedAt;
      } else if (lastServerBalanceRef.current !== null) {
        // Пока запись активна, billing ещё не списывает её минуты. Поэтому разница
        // серверного баланса отражает пополнения (или другие операции по счёту).
        balanceAdjustmentRef.current += normalized - lastServerBalanceRef.current;
      }

      lastServerBalanceRef.current = normalized;
      const nextRemaining = calculateVirtualRemaining();
      setVirtualRemaining(nextRemaining);
      return nextRemaining;
    },
    [calculateVirtualRemaining],
  );

  const clearBalanceTracking = useCallback(() => {
    balanceStartedAtRef.current = null;
    balanceInitialRef.current = null;
    balanceAdjustmentRef.current = 0;
    lastServerBalanceRef.current = null;
    balanceWarningCheckRef.current = false;
    autoStopInFlightRef.current = false;
    setVirtualRemaining(undefined);
    setIsBalanceTracking(false);
    setIsBalanceWarning(false);
  }, []);

  const clearPendingDebit = useCallback(() => {
    removePendingDebitFromStorage(pendingDebitStorageKey);
    setPendingDebit(null);
  }, [pendingDebitStorageKey]);

  const savePendingDebit = useCallback(
    (minutes: number) => {
      if (!scheduledEndAt || scheduledEndAt <= Date.now() || !Number.isFinite(minutes) || minutes <= 0) {
        return;
      }

      const pending: PendingSupervisorDebit = {
        consultationId,
        expiresAt: scheduledEndAt,
        minutes,
        roomName,
      };

      if (typeof window !== 'undefined') {
        try {
          window.localStorage.setItem(pendingDebitStorageKey, JSON.stringify(pending));
        } catch {
          // В текущей вкладке прогнозный остаток остаётся доступным и без persistence.
        }
      }
      setPendingDebit(pending);
    },
    [consultationId, pendingDebitStorageKey, roomName, scheduledEndAt],
  );

  const calculateTrackedMinutes = useCallback((now = Date.now()) => {
    const startedAt = balanceStartedAtRef.current;
    if (startedAt === null) {
      return 0;
    }

    return Math.max(0, Math.ceil((now - startedAt) / MINUTE_MS));
  }, []);

  const isSupervisorAllowed = Boolean(statusData?.active) || (supervisorAccessData?.allowed ?? true);
  const isSupervisorLoading = isStarting || isStopping;

  const refreshSupervisorBalance = useCallback(async () => {
    const result = await refetchRecordingAccess();
    const remaining = normalizeRemaining(result.data?.remaining);
    if (remaining === undefined) {
      return undefined;
    }

    return updateVirtualRemaining(remaining, balanceStartedAtRef.current ?? Date.now());
  }, [refetchRecordingAccess, updateVirtualRemaining]);

  const stopSupervisor = useCallback(async () => {
    const fallbackDurationMin = calculateTrackedMinutes();
    const result = await stopRecording({ recordingId: roomName }).unwrap();
    const durationMin = result.durationMin;
    const pendingMinutes =
      typeof durationMin === 'number' && Number.isFinite(durationMin) && durationMin > 0
        ? durationMin
        : fallbackDurationMin;

    if (result.message === 'recording stopped') {
      savePendingDebit(pendingMinutes);
    }

    clearBalanceTracking();
    sendRecordingStatusUpdate?.('stopped');
    return result;
  }, [
    calculateTrackedMinutes,
    clearBalanceTracking,
    roomName,
    savePendingDebit,
    sendRecordingStatusUpdate,
    stopRecording,
  ]);

  const stopSupervisorForExhaustedBalance = useCallback(async () => {
    if (autoStopInFlightRef.current) {
      return;
    }

    autoStopInFlightRef.current = true;
    try {
      await stopSupervisor();
    } catch {
      // Следующая минутная проверка повторит безопасный idempotent-запрос stop.
      autoStopInFlightRef.current = false;
    }
  }, [stopSupervisor]);

  const startSupervisor = useCallback(() => {
    clearPendingDebit();
    return startRecording({ recordingId: roomName });
  }, [clearPendingDebit, roomName, startRecording]);

  useEffect(() => {
    if (supervisorSend && !isPsychologist) {
      setNotificationClosed(false);
    }
  }, [supervisorSend, isPsychologist]);

  useEffect(() => {
    setNotificationClosed(false);
  }, [supervisorNotification]);

  useEffect(() => {
    if (supervisorNotification?.type === 'accepted') {
      void startSupervisor();
    }
  }, [startSupervisor, supervisorNotification]);

  useEffect(() => {
    if (startData?.status === 'ok' && isPsychologist) {
      const remaining = normalizeRemaining(startData.remaining);
      if (remaining !== undefined) {
        updateVirtualRemaining(remaining, Date.now(), true);
        setIsBalanceTracking(true);
      }
      sendRecordingStatusUpdate?.('started');
    }
  }, [isPsychologist, sendRecordingStatusUpdate, startData, updateVirtualRemaining]);

  useEffect(() => {
    // A client can open the same room in a browser profile where the psychologist
    // previously started the supervisor. Do not restore that psychologist-only
    // pending debit: its access query is intentionally skipped for clients, and
    // calling refetch on a skipped RTK Query hook throws error #38.
    if (!isPsychologist) {
      setPendingDebit(null);
      return;
    }

    const storedPendingDebit = readPendingDebit(pendingDebitStorageKey, roomName, consultationId);
    setPendingDebit(storedPendingDebit);
  }, [consultationId, isPsychologist, pendingDebitStorageKey, roomName]);

  useEffect(() => {
    if (!isPsychologist || !pendingDebit) {
      return;
    }

    const expiresIn = pendingDebit.expiresAt - Date.now();
    if (expiresIn <= 0) {
      clearPendingDebit();
      void refetchRecordingAccess();
      return;
    }

    const timeoutId = window.setTimeout(() => {
      clearPendingDebit();
      void refetchRecordingAccess();
    }, expiresIn);

    return () => window.clearTimeout(timeoutId);
  }, [clearPendingDebit, isPsychologist, pendingDebit, refetchRecordingAccess]);

  useEffect(() => {
    if (!isPsychologist || !pendingDebit) {
      return;
    }

    void refetchRecordingAccess();
    const intervalId = window.setInterval(() => {
      void refetchRecordingAccess();
    }, MINUTE_MS);

    return () => window.clearInterval(intervalId);
  }, [isPsychologist, pendingDebit, refetchRecordingAccess]);

  useEffect(() => {
    if (statusData?.active) {
      lastKnownActiveRef.current = true;
      const remaining = normalizeRemaining(supervisorAccessData?.remaining);
      if (isPsychologist && !isBalanceTracking && remaining !== undefined) {
        updateVirtualRemaining(remaining, statusData.startedAt ?? Date.now(), true);
        setIsBalanceTracking(true);
      }
      return;
    }

    if (lastKnownActiveRef.current && !isStopping) {
      lastKnownActiveRef.current = false;
      clearBalanceTracking();
    }
  }, [
    clearBalanceTracking,
    isBalanceTracking,
    isPsychologist,
    isStopping,
    statusData?.active,
    statusData?.startedAt,
    supervisorAccessData?.remaining,
    updateVirtualRemaining,
  ]);

  useEffect(() => {
    if (!isBalanceTracking) {
      return;
    }

    const update = () => setVirtualRemaining(calculateVirtualRemaining());
    update();
    const intervalId = window.setInterval(update, 1000);
    return () => window.clearInterval(intervalId);
  }, [calculateVirtualRemaining, isBalanceTracking]);

  useEffect(() => {
    if (!isBalanceTracking || virtualRemaining === undefined) {
      return;
    }

    if (virtualRemaining > MINIMUM_REMAINING_MINUTES) {
      balanceWarningCheckRef.current = false;
      setIsBalanceWarning(false);
      return;
    }

    if (balanceWarningCheckRef.current) {
      return;
    }

    balanceWarningCheckRef.current = true;
    void refreshSupervisorBalance()
      .then((remaining) => {
        if (remaining === undefined || remaining <= MINIMUM_REMAINING_MINUTES) {
          setIsBalanceWarning(true);
          if (remaining !== undefined && remaining <= 0) {
            void stopSupervisorForExhaustedBalance();
          }
          return;
        }

        balanceWarningCheckRef.current = false;
        setIsBalanceWarning(false);
      })
      .catch(() => setIsBalanceWarning(true));
  }, [isBalanceTracking, refreshSupervisorBalance, stopSupervisorForExhaustedBalance, virtualRemaining]);

  useEffect(() => {
    if (!isBalanceWarning || !isBalanceTracking) {
      return;
    }

    const intervalId = window.setInterval(() => {
      void refreshSupervisorBalance()
        .then((remaining) => {
          if (remaining === undefined || remaining <= 0) {
            void stopSupervisorForExhaustedBalance();
            return;
          }

          if (remaining > MINIMUM_REMAINING_MINUTES) {
            balanceWarningCheckRef.current = false;
            setIsBalanceWarning(false);
          }
        })
        .catch(() => {
          void stopSupervisorForExhaustedBalance();
        });
    }, MINUTE_MS);

    return () => window.clearInterval(intervalId);
  }, [isBalanceTracking, isBalanceWarning, refreshSupervisorBalance, stopSupervisorForExhaustedBalance]);

  const confirmStopSupervisor = useCallback(() => {
    setIsConfirmCloseOpen(false);
    void stopSupervisor();
    setSupervisorNotification(null);
    setIsTooltipVisible(true);
    setTimeout(() => setIsTooltipVisible(false), 3000);
  }, [stopSupervisor]);

  const rejectStopSupervisor = useCallback(() => {
    setIsConfirmCloseOpen(false);
  }, []);

  const confirmStartSupervisorGroup = useCallback(() => {
    setIsConfirmStartSupervisorOpen(false);
    setSupervisorNotification({ type: 'accepted' });
  }, []);

  const rejectStartSupervisorGroup = useCallback(() => {
    setIsConfirmStartSupervisorOpen(false);
  }, []);

  const handleSupervisorClick = useCallback(() => {
    if (!isSupervisorAllowed) {
      return;
    }

    const isActive = supervisorNotification?.type === 'accepted' || statusData?.active;

    if (isActive) {
      setIsConfirmCloseOpen(true);
      return;
    }

    if (isGroupConsultationFlag && isPsychologist) {
      setIsConfirmStartSupervisorOpen(true);
      return;
    }

    sendSupervisorRequest?.();
    setSupervisorNotification({ type: 'request_sent' });
  }, [
    isGroupConsultationFlag,
    isPsychologist,
    isSupervisorAllowed,
    sendSupervisorRequest,
    statusData,
    supervisorNotification,
  ]);

  const backendRemaining = normalizeRemaining(supervisorAccessData?.remaining);
  const pendingAdjustedRemaining = pendingDebit
    ? backendRemaining === undefined
      ? undefined
      : Math.max(0, backendRemaining - pendingDebit.minutes)
    : backendRemaining;

  return {
    statusData,
    refetchRecordingStatus,
    isSupervisorAllowed,
    isSupervisorLoading,
    supervisorRemaining: virtualRemaining ?? pendingAdjustedRemaining,
    isSupervisorBalanceWarning: isBalanceWarning,
    isTooltipVisible,
    supervisorNotification,
    setSupervisorNotification,
    supervisorSend,
    setSupervisorSend,
    notificationClosed,
    setNotificationClosed,
    handleSupervisorClick,
    confirmStopSupervisor,
    rejectStopSupervisor,
    confirmStartSupervisorGroup,
    rejectStartSupervisorGroup,
    sendRecordingStatusUpdate,
    isConfirmStopSupervisorOpen: isConfirmCloseOpen,
    isConfirmStartSupervisorOpen,
  };
}
